import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { PaginatedResponse } from '@/lib/api-types'
import type {
  KnowledgeArticle,
  KnowledgeArticleInput,
  KnowledgeBase,
  KnowledgeBaseInput,
  KnowledgeBaseWithArticles,
  KnowledgeBaseWithContentInput,
} from '@/types/knowledge'

export const knowledgeKeys = {
  list: (orgId: string | null) => ['knowledge-bases', 'list', orgId] as const,
  detail: (orgId: string | null, kbId: string | null) =>
    ['knowledge-bases', 'detail', orgId, kbId] as const,
}

export function useKnowledgeBases(orgId: string | null, enabled = true) {
  return useQuery<PaginatedResponse<KnowledgeBase>>({
    queryKey: knowledgeKeys.list(orgId),
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<KnowledgeBase>>(
        `/orgs/${orgId}/knowledge-bases`,
        { params: { limit: 100 } }
      )
      return res.data
    },
    enabled: !!orgId && enabled,
    staleTime: 30_000,
  })
}

export function useKnowledgeBaseDetail(
  orgId: string | null,
  kbId: string | null
) {
  return useQuery<KnowledgeBaseWithArticles>({
    queryKey: knowledgeKeys.detail(orgId, kbId),
    queryFn: async () => {
      const res = await api.get<KnowledgeBaseWithArticles>(
        `/orgs/${orgId}/knowledge-bases/${kbId}`
      )
      return res.data
    },
    enabled: !!orgId && !!kbId,
    staleTime: 30_000,
  })
}

export function useCreateKnowledgeBase(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: KnowledgeBaseWithContentInput) => {
      const { content, ...baseInput } = data
      const res = await api.post<KnowledgeBase>(
        `/orgs/${orgId}/knowledge-bases`,
        baseInput
      )

      if (content?.trim()) {
        await api.post<KnowledgeArticle>(
          `/orgs/${orgId}/knowledge-bases/${res.data.id}/articles`,
          {
            title: res.data.name,
            content: content.trim(),
          }
        )
      }

      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: knowledgeKeys.list(orgId) })
    },
  })
}

export function useUpdateKnowledgeBase(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      kbId,
      data,
    }: {
      kbId: string
      data: KnowledgeBaseInput
    }) => {
      const res = await api.put<KnowledgeBase>(
        `/orgs/${orgId}/knowledge-bases/${kbId}`,
        data
      )
      return res.data
    },
    onSuccess: (_result, { kbId }) => {
      void qc.invalidateQueries({ queryKey: knowledgeKeys.list(orgId) })
      void qc.invalidateQueries({
        queryKey: knowledgeKeys.detail(orgId, kbId),
      })
    },
  })
}

export function useDeleteKnowledgeBase(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (kbId: string) => {
      await api.delete(`/orgs/${orgId}/knowledge-bases/${kbId}`)
      return kbId
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: knowledgeKeys.list(orgId) })
    },
  })
}

export function useCreateArticle(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      kbId,
      data,
    }: {
      kbId: string
      data: KnowledgeArticleInput
    }) => {
      const res = await api.post<KnowledgeArticle>(
        `/orgs/${orgId}/knowledge-bases/${kbId}/articles`,
        data
      )
      return res.data
    },
    onSuccess: (_result, { kbId }) => {
      void qc.invalidateQueries({
        queryKey: knowledgeKeys.detail(orgId, kbId),
      })
      void qc.invalidateQueries({ queryKey: knowledgeKeys.list(orgId) })
    },
  })
}

export function useUpdateArticle(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      kbId,
      articleId,
      data,
    }: {
      kbId: string
      articleId: string
      data: KnowledgeArticleInput
    }) => {
      const res = await api.put<KnowledgeArticle>(
        `/orgs/${orgId}/knowledge-bases/${kbId}/articles/${articleId}`,
        data
      )
      return res.data
    },
    onSuccess: (_result, { kbId }) => {
      void qc.invalidateQueries({
        queryKey: knowledgeKeys.detail(orgId, kbId),
      })
      void qc.invalidateQueries({ queryKey: knowledgeKeys.list(orgId) })
    },
  })
}

export function useDeleteArticle(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      kbId,
      articleId,
    }: {
      kbId: string
      articleId: string
    }) => {
      await api.delete(
        `/orgs/${orgId}/knowledge-bases/${kbId}/articles/${articleId}`
      )
      return { kbId, articleId }
    },
    onSuccess: (_result, { kbId }) => {
      void qc.invalidateQueries({
        queryKey: knowledgeKeys.detail(orgId, kbId),
      })
      void qc.invalidateQueries({ queryKey: knowledgeKeys.list(orgId) })
    },
  })
}
